/**
 * useSavePuzzleDashResult
 *
 * POST /fun_challenges/v1/puzzle_dash_results/
 *
 * Idempotent on `session_id` (history row) + `(history_id, user_id)`
 * (per-player rows) server-side via update_or_create. Both clients fire
 * this on `fun_session_end`; the second call no-ops.
 *
 * N-player-ready payload: the per-participant data lives in `players[]`
 * rather than `p1_*` / `p2_*` columns. v1 ships with `players.length === 2`
 * (challenger + acceptor); future N-player races just send more entries.
 *
 * Auth: bearer auto-injected by `ChessRestClient`. Server rejects writes
 * where `request.user.user_id` is neither `player1_id` nor `player2_id`.
 */

import {useMutation} from '@tanstack/react-query';

import ChessRestClient from '@/Api/ChessRestClient';
import type {PuzzleAttempt} from '@/Screens/FunChallenges/util/attempts';

export interface PuzzleDashPlayerResultPayload {
  user_id: number;
  score: number;          // correct count
  wrong: number;
  accuracy: number;       // 0.0..1.0
  solved_ids: number[];
  failed_ids: number[];
  attempts: PuzzleAttempt[];
  eliminated: boolean;    // true iff this player hit maxWrong before session end
  finished_at?: string | null;   // ISO timestamp
}

export interface SavePuzzleDashResultPayload {
  session_id: string;
  game_type: string;                  // 'puzzle_dash'
  /**
   * Kept for the game-type-agnostic history row (cc_fun_challenges_history
   * has fixed player1_id/player2_id columns). For N>2 in the future, any
   * two ids fit here — the rest are recoverable from `players[]`.
   */
  player1_id: number;
  player2_id: number;
  result: 'p1' | 'p2' | 'draw';
  reason: 'target_reached' | 'mistake_out' | 'forfeit' | 'time_up';
  puzzle_ids: number[];
  players: PuzzleDashPlayerResultPayload[];
  target_correct: number;
  max_wrong: number;
  end_reason: 'target_reached' | 'mistake_out' | 'forfeit' | 'time_up';
  time_control_sec: number;           // 600 (the 10-min safety cap)
}

export interface SavePuzzleDashResultResponse {
  ok: boolean;
  history_id?: number;
  error?: string;
}

export default function useSavePuzzleDashResult(
  onSuccess?: (data: SavePuzzleDashResultResponse) => void,
  onError?: (err: unknown) => void,
) {
  return useMutation<
    SavePuzzleDashResultResponse,
    unknown,
    SavePuzzleDashResultPayload
  >(
    async (payload: SavePuzzleDashResultPayload) => {
      const response = await ChessRestClient.post(
        'fun_challenges/v1/puzzle_dash_results/',
        payload,
      );
      return response.data as SavePuzzleDashResultResponse;
    },
    {
      onSuccess: data => {
        if (onSuccess) onSuccess(data);
      },
      onError: err => {
        if (onError) onError(err);
      },
    },
  );
}
